import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Home, DollarSign, Calendar, Lock, Copy, Check, Share2, MessageCircle } from 'lucide-react';

const API   = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

export default function PrivateListingView() {
  const { token } = useParams();
  const [listing, setListing]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    fetch(`${API}/api/properties/private/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setListing(d); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [token]);

  const pageUrl = window.location.href;

  const copyLink = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-[#094f6f] border-t-transparent rounded-full" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: '#f4f6f9' }}>
      <div>
        <div className="w-16 h-16 rounded-2xl bg-gray-200 flex items-center justify-center mx-auto mb-5">
          <Lock className="w-8 h-8 text-gray-400" />
        </div>
        <h1 className="text-2xl font-black text-gray-900 mb-2">Listing not available</h1>
        <p className="text-gray-500 text-sm mb-6">This private listing may have been removed or the link is incorrect.</p>
        <Link to="/" className="inline-block px-6 py-3 rounded-xl font-bold text-white text-sm hover:opacity-90"
          style={{ background: BRAND }}>Browse public listings →</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      {/* Nav */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.4rem', width: 'auto', marginLeft: '0.04em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </Link>
          <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
            <Lock className="w-3 h-3" /> Private Listing
          </span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Images */}
        {listing.images?.length > 0 && (
          <div className="rounded-3xl overflow-hidden mb-6 h-72 bg-gray-200">
            <img src={listing.images[0]} alt={listing.name}
              className="w-full h-full object-cover" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-start justify-between gap-3 mb-3">
                <h1 className="text-2xl font-black text-gray-900">{listing.name}</h1>
                <span className="flex-shrink-0 text-xs font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Private
                </span>
              </div>
              <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.city}</span>
                <span className="flex items-center gap-1"><Home className="w-4 h-4" /> {listing.room_type}</span>
                <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> A${listing.price_per_week}/wk</span>
                {listing.available_from && (
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Available {listing.available_from}</span>
                )}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{listing.description}</p>
            </div>

            {listing.amenities?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="font-bold text-gray-900 text-sm mb-3">Amenities</p>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map(a => (
                    <span key={a} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-3xl font-black text-[#094f6f] mb-0.5">A${listing.price_per_week}</p>
              <p className="text-xs text-gray-400 mb-5">per week</p>

              {/* Single CTA — takes user to the main login page, then redirects back here */}
              <Link
                to="/login"
                state={{ from: window.location.pathname + window.location.search }}
                className="block w-full text-center py-3.5 rounded-xl font-bold text-white text-sm hover:opacity-90 transition-all hover:scale-[1.01] mb-2"
                style={{ background: BRAND }}>
                Log in to enquire
              </Link>
              <Link
                to="/register/student"
                state={{ from: window.location.pathname + window.location.search }}
                className="block w-full text-center py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                Create a free account
              </Link>
              <p className="text-center text-xs text-gray-400 mt-3">
                Already have an account? Log in above to message the landlord directly.
              </p>
            </div>

            {/* Share box */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5" /> Share this listing
              </p>
              <div className="flex gap-2 mb-3">
                <a href={`https://wa.me/?text=${encodeURIComponent('Check out this property: ' + pageUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50">
                  <MessageCircle className="w-3.5 h-3.5 text-green-500" /> WhatsApp
                </a>
                <a href={`sms:?&body=${encodeURIComponent('Check out this property: ' + pageUrl)}`}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50">
                  SMS
                </a>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 truncate text-gray-500">
                  {pageUrl}
                </code>
                <button onClick={copyLink}
                  className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                  style={{ background: BRAND }}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
